// Globals
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { ChangeEvent, memo, useEffect, useRef, useState } from 'react';

// Models
import { IAddress } from '@/models/place.model';

// Components
import { Input, TInputRef } from '@/ui';

type TPlaceAutocompleteProps = {
  onChange?: (place: IAddress | string | null) => void;
  value?: string | IAddress;
  placeholder?: string;
};

export const PlaceAutocomplete = ({ onChange, value, placeholder = '' }: TPlaceAutocompleteProps) => {
  const [placeAutocomplete, setPlaceAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const places = useMapsLibrary('places');
  const [formattedAddress, setFormattedAddress] = useState(() => (typeof value === 'string' ? value : value?.name));

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options: google.maps.places.AutocompleteOptions = {
      fields: ['place_id', 'formatted_address', 'geometry'],
      componentRestrictions: { country: 'ua' },
    };

    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
  }, [places]);

  useEffect(() => {
    if (!placeAutocomplete) return;

    const handlePlaceChanged = () => {
      const result = placeAutocomplete.getPlace();
      if (!result?.formatted_address || !result?.place_id) return;
      setFormattedAddress?.(result.formatted_address);
      onChange?.({
        name: result.formatted_address,
        place_id: result.place_id,
        latitude: result?.geometry?.location?.lat() ?? 0,
        longitude: result?.geometry?.location?.lng() ?? 0,
      });
    };

    placeAutocomplete.addListener('place_changed', handlePlaceChanged);
  }, [onChange, placeAutocomplete]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange?.(null);
    return setFormattedAddress?.(event.target.value);
  };

  const setRef = (node: TInputRef | null) => {
    if (node?.input) {
      inputRef.current = node.input;
    }
  };

  return <Input ref={setRef} placeholder={placeholder} onChange={handleChange} value={formattedAddress} />;
};

export default memo(PlaceAutocomplete);
